import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { doc, setDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuArrowRight, LuLoaderCircle, LuToggleLeft, LuToggleRight } from 'react-icons/lu';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { db } from '../../config/firebaseConfig';
import { useTranslation } from '../../i18n/LanguageContext';
import { StatusBadge } from './components/CustomerCell';
import { customerName, isActiveCustomer } from './customerColumns';

/** Confirms switching a customer between "aktif" and "inaktif". */
const ChangeCustomerState = ({ customer, onClose, onChanged }) => {
  const { t, language } = useTranslation();
  const [saving, setSaving] = useState(false);
  const isActive = isActiveCustomer(customer);
  const newStatus = isActive ? 'inaktif' : 'aktif';

  const confirmStatusChange = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'Customers', customer.id), { Durum: newStatus }, { merge: true });
      toast.success(t('customers.statusChange.success'));
      onChanged(customer.id, newStatus);
      onClose();
    } catch (error) {
      console.error('Error updating customer status:', error.message);
      toast.error(t('customers.statusChange.error'));
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={() => !saving && onClose()}
      size="md"
      title={t('customers.statusChange.title')}
      subtitle={customerName(customer)}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon
          gradient={isActive ? 'tw-from-rose-400 tw-to-rose-600 tw-shadow-rose-500/30' : 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30'}
          size="xs"
        >
          {isActive
            ? <LuToggleLeft aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
            : <LuToggleRight aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />}
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>{t('customers.statusChange.cancel')}</Button>
          <Button variant={isActive ? 'danger' : 'success'} onClick={confirmStatusChange} disabled={saving} className="max-sm:tw-flex-1">
            {saving && <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />}
            {t('customers.statusChange.confirm')}
          </Button>
        </>
      )}
    >
      <p className="tw-text-[15px] tw-leading-relaxed tw-text-slate-700 dark:tw-text-slate-200">
        {isActive ? t('customers.statusChange.toInactive') : t('customers.statusChange.toActive')}
      </p>
      <div className="tw-mt-4 tw-flex tw-items-center tw-justify-center tw-gap-3 tw-rounded-2xl tw-bg-slate-900/[0.03] tw-px-4 tw-py-3 dark:tw-bg-white/[0.04]">
        <StatusBadge value={customer['Durum'] ? String(customer['Durum']) : undefined} />
        <LuArrowRight aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" />
        <StatusBadge value={newStatus} />
      </div>
    </Modal>
  );
};

ChangeCustomerState.propTypes = {
  customer: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
};

export default ChangeCustomerState;
